import { useTrackImpressionEventByIntersection } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { config } from "shared/data";

import type { PlusWidgetsSampleContentType } from "scmp-app/components/plus/plus-widget";
import { PlusWidgetHighlights } from "scmp-app/components/plus/plus-widget/plus-widget-highlights";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { ContentDiscoveryWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import { useRosettaUser } from "scmp-app/lib/rosetta/hooks/rosetta-user";
import type { plusWidgetWithoutAccess$key } from "scmp-app/queries/__generated__/plusWidgetWithoutAccess.graphql";

import {
  Container,
  HighlightsFromScmpPlus,
  Intro,
  IntroText,
  StyledLogoPlus,
  StyledPlusWidgetQuotes,
  Title,
  TryForFreeButtonLink,
} from "./styles";

type Props = {
  className?: string;
  pageType: "homepage" | "section" | "topic";
  reference: plusWidgetWithoutAccess$key;
  sampleContent: PlusWidgetsSampleContentType;
};
export const PlusWidgetWithoutAccess: FunctionComponent<Props> = ({
  className,
  pageType,
  reference,
}) => {
  const { isSCMPDigitalOnlySubscriber } = useRosettaUser();
  const ga4ImpressionEvent = useCallback<() => ContentDiscoveryWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "subs",
      customized_parameters: {
        call_to_action: isSCMPDigitalOnlySubscriber ? "upgrade" : "plus trial",
        page_type: pageType,
        trigger_point: "content_discovery",
      },
      subcategory: "entry_point",
    }),
    [isSCMPDigitalOnlySubscriber, pageType],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<ContentDiscoveryWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: ga4ImpressionEvent,
      options: {
        isSendGA4Tracking: true,
        shouldSendOnce: true,
      },
    });

  const handleTrialButtonClickEvent = () => {
    sendGA4Tracking({
      action: "click",
      category: "subs",
      customized_parameters: {
        call_to_action: isSCMPDigitalOnlySubscriber ? "upgrade" : "plus trial",
        trigger_point: "content_discovery",
      },
      subcategory: "entry_point",
    });
  };

  const data = useFragment(
    graphql`
      fragment plusWidgetWithoutAccess on Query
      @argumentDefinitions(
        applicationId: { type: "String!" }
        dailyPulseTypeUuId: { type: "String!" }
        highlightQueueName: { type: "String!" }
        plusNewsAgendaStartDate: { type: "Timestamp!" }
        plusNewsAgendaEndDate: { type: "Timestamp!" }
        plusNewsAgendaTypes: { type: "[String]!" }
      ) {
        ...plusWidgetDailyPulseQuery @arguments(dailyPulseTypeUuId: $dailyPulseTypeUuId)
        ...plusWidgetHighlightsQuery @arguments(queueName: $highlightQueueName)
        ...plusWidgetNewsAgendaQuery
          @arguments(
            applicationId: $applicationId
            plusNewsAgendaEndDate: $plusNewsAgendaEndDate
            plusNewsAgendaStartDate: $plusNewsAgendaStartDate
            plusNewsAgendaTypes: $plusNewsAgendaTypes
          )
      }
    `,
    reference,
  );

  return (
    <Container className={className} ref={captureTrackImpressionEventTargetElement}>
      <StyledLogoPlus />
      <Intro>
        <IntroText>
          SCMP Plus is a new premium news platform that gives you an{" "}
          <span>all-inclusive edge to stay ahead on China news.</span> To access our exclusive
          content you’ll need to subscribe.
        </IntroText>
        <TryForFreeButtonLink
          onClick={handleTrialButtonClickEvent}
          pathname={
            isSCMPDigitalOnlySubscriber
              ? `${config.account.scmpAccountHost}/manage/subscription?upgrade=true`
              : "https://subscribe.scmp.com/plus"
          }
        >
          {isSCMPDigitalOnlySubscriber ? "UPGRADE" : "TRY FOR FREE"}
        </TryForFreeButtonLink>
      </Intro>
      <StyledPlusWidgetQuotes />
      <HighlightsFromScmpPlus>
        <Title>Highlights from SCMP Plus</Title>
        <PlusWidgetHighlights pageType={pageType} reference={data} />
      </HighlightsFromScmpPlus>
    </Container>
  );
};

PlusWidgetWithoutAccess.displayName = "PlusWidgetWithoutAccess";
